import Donut from "../Donut/Donut";
import { getColor } from "./scoreColor";
import styles from "./EmScore.module.css";

const SCORE_RANGE_MAX = 10;
const INSIDE_LABEL_FONT_RATIO = 0.27;
const INSIDE_VALUE_FONT_RATIO = 0.82;
const VALUE_ONLY_FONT_RATIO = 1.02;
const INSIDE_LABEL_OFFSET_RATIO = 0.44;
const INSIDE_VALUE_OFFSET_RATIO = 0.14;
const STROKE_WIDTH_RATIO = {
  standard: 0.12,
  inside: 0.1,
} as const;

type ScoreProps = {
  score?: number;
  status?: string;
  size?: number;
  inside?: string;
  after?: string;
  labelVariant?: "default" | "compact";
};

const getScoreText = (score: number) => {
  if (!Number.isFinite(score) || score <= 0) {
    return "-";
  }

  return score === 10 ? "10" : score.toFixed(1);
};

const getSizeClassName = (size: number) => {
  if (size <= 3.25) {
    return styles.sizeSmall;
  }

  if (size >= 5) {
    return styles.sizeLarge;
  }

  return styles.sizeMedium;
};

const getStrokeWidth = (size: number, hasInsideLabel: boolean) => size * 100 * (hasInsideLabel ? STROKE_WIDTH_RATIO.inside : STROKE_WIDTH_RATIO.standard);

const EmScore = ({ score = 0, status = "finished", size = 6, inside, after, labelVariant = "default" }: ScoreProps) => {
  const safeScore = Number.isFinite(score) ? score : 0;
  const scoreText = getScoreText(safeScore);

  return (
    <div className={[styles.root, getSizeClassName(size)].join(" ")}>
      <div className={styles.chart}>
        <Donut
          isAnimated={status === "ongoing"}
          max={SCORE_RANGE_MAX}
          min={0}
          size={size}
          strokeWidth={getStrokeWidth(size, Boolean(inside))}
          title={`Score ${scoreText}`}
          tone={getColor(safeScore)}
          value={safeScore}
        >
          {({ center, innerRadius }) => {
            const labelFontSize = innerRadius * INSIDE_LABEL_FONT_RATIO;
            const labelY = center - innerRadius * INSIDE_LABEL_OFFSET_RATIO;
            const valueFontSize = innerRadius * (inside ? INSIDE_VALUE_FONT_RATIO : VALUE_ONLY_FONT_RATIO);
            const valueY = center + (inside ? innerRadius * INSIDE_VALUE_OFFSET_RATIO : 0);

            return inside ? (
              <>
                <text
                  dominantBaseline='middle'
                  fill='var(--color-text-muted)'
                  fontFamily='var(--font-family-base)'
                  fontSize={labelFontSize}
                  fontWeight='600'
                  letterSpacing={labelFontSize * 0.08}
                  textAnchor='middle'
                  x={center}
                  y={labelY}
                >
                  {inside}
                </text>
                <text
                  dominantBaseline='middle'
                  fill='var(--color-text)'
                  fontFamily='"Unna", serif'
                  fontSize={valueFontSize}
                  fontWeight='700'
                  textAnchor='middle'
                  x={center}
                  y={valueY}
                >
                  {scoreText}
                </text>
              </>
            ) : (
              <text
                dominantBaseline='middle'
                fill='var(--color-text)'
                fontFamily='"Unna", serif'
                fontSize={valueFontSize}
                fontWeight='700'
                textAnchor='middle'
                x={center}
                y={valueY}
              >
                {scoreText}
              </text>
            );
          }}
        </Donut>
      </div>
      {after && (
        <div className={[styles.label, styles[`label_${labelVariant}`]].filter(Boolean).join(" ")}>
          {after}
        </div>
      )}
    </div>
  );
};

export default EmScore;
